import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import {
  Folder,
  Hash,
  Plus,
  Trash2,
  CheckSquare,
  Inbox,
  Sparkles,
  Loader2,
  Search,
  ChevronLeft,
  Filter as FilterIcon,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { useToast } from "../ui/toast";
import { useModal, Modal } from "../ui/modal";
import { useT } from "../i18n";
import { Button, EmptyState } from "../ui";

interface Note {
  id: string;
  title: string | null;
  body_md: string;
  tags: string[];
  category_id: string | null;
  project_id: string | null;
  embedding_status: string;
  promoted_task_id: string | null;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  name: string;
}

type Filter = { kind: "all" } | { kind: "cat"; id: string } | { kind: "tag"; t: string };

// Drop-Ziel „Alle Notizen" = Kategorie entfernen (category_id → null).
const DROP_ALL = "__all__";

export default function NotesPage() {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const modal = useModal();
  const t = useT();
  const [notes, setNotes] = useState<Note[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [filter, setFilter] = useState<Filter>({ kind: "all" });
  const [q, setQ] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [dragNote, setDragNote] = useState<Note | null>(null);

  // Drag nur auf Desktop aktiv — auf Touch wuerde ein PointerSensor das
  // vertikale Scrollen der Liste kapern. Drop-Zonen liegen ohnehin in der
  // Desktop-Rail (mobile filtert via Modal).
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // activationConstraint distance:6 → Klick (=Oeffnen) bleibt Klick, erst ab
  // 6px Bewegung startet der Drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Kategorie einer Notiz per Drop setzen — exakt derselbe Call wie das
  // (nun entfernte) Editor-Dropdown: PATCH /notes/:id { category_id }.
  const moveNoteToCategory = useCallback(
    async (id: string, newCat: string | null) => {
      const target = notes.find((n) => n.id === id);
      if (!target || (target.category_id ?? null) === newCat) return;
      const prev = notes;
      // Optimistisch: category_id setzen und — falls ein Kategorie-Filter aktiv
      // ist und die Notiz nicht mehr passt — aus der Liste nehmen.
      setNotes((cur) =>
        cur
          .map((n) => (n.id === id ? { ...n, category_id: newCat } : n))
          .filter((n) => !(filter.kind === "cat" && n.id === id && filter.id !== newCat))
      );
      try {
        await api.patch<Note>(`/notes/${id}`, { category_id: newCat });
        const label = newCat
          ? cats.find((c) => c.id === newCat)?.name ?? t.notes.kategorie
          : null;
        toast.ok(label ? t.notes.verschobenNach(label) : t.notes.kategorieEntfernt);
      } catch (e) {
        setNotes(prev);
        toast.err(t.notes.kategorieNichtGeaendert, (e as Error).message);
      }
    },
    [notes, cats, filter, toast, t]
  );

  const onDragStart = (e: DragStartEvent) => {
    setDragNote(notes.find((n) => n.id === e.active.id) ?? null);
  };
  const onDragEnd = (e: DragEndEvent) => {
    setDragNote(null);
    const overId = e.over?.id as string | undefined;
    if (!overId) return;
    moveNoteToCategory(e.active.id as string, overId === DROP_ALL ? null : overId);
  };

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q);
    if (filter.kind === "cat") params.set("category_id", filter.id);
    if (filter.kind === "tag") params.set("tag", filter.t);
    setNotes(await api.get<Note[]>(`/notes?${params.toString()}`));
  }, [q, filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get<Category[]>("/categories").then(setCats).catch(() => setCats([]));
  }, []);

  const selected = noteId ? notes.find((n) => n.id === noteId) ?? null : null;

  // Aktiver Filter als Label fuer Mobile-Chip.
  const filterLabel =
    filter.kind === "all"
      ? t.notes.alleNotizen
      : filter.kind === "cat"
      ? (cats.find((c) => c.id === filter.id)?.name ?? t.notes.kategorie)
      : `#${filter.t}`;

  useEffect(() => {
    // Auto-Navigate zur ersten Notiz NUR auf Desktop (>=768px). Auf mobile
    // soll der User die Liste sehen und manuell waehlen koennen.
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
      return;
    }
    if (!noteId && notes[0]) {
      navigate(`/n/${notes[0].id}`, { replace: true });
    }
  }, [noteId, notes, navigate]);

  const allTags = useMemo(() => {
    const s = new Set<string>();
    notes.forEach((n) => n.tags.forEach((t) => s.add(t)));
    return [...s].sort();
  }, [notes]);

  const newNote = async () => {
    const n = await api.post<Note>("/notes", {
      body_md: "Neue Notiz",
      title: "",
      tags: [],
      category_id: filter.kind === "cat" ? filter.id : null,
    });
    await load();
    navigate(`/n/${n.id}`);
    toast.ok(t.notes.notizAngelegt);
  };

  const delNote = async (n: Note) => {
    const ok = await modal.confirm({
      title: t.notes.notizLoeschenFrage,
      description: n.title || n.body_md.slice(0, 80),
      confirmLabel: t.common.loeschen,
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/notes/${n.id}`);
    toast.ok(t.notes.notizGeloescht);
    setNotes((s) => s.filter((x) => x.id !== n.id));
    if (selected?.id === n.id) navigate("/n");
  };

  return (
   <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
    <div className="grid grid-cols-1 md:grid-cols-[200px_320px_1fr] h-full">
      {/* Spalte 1: Kategorien-Sidebar - desktop-only, mobile geht via Filter-Modal.
          Kategorie-Zeilen + „Alle Notizen" sind Drop-Zonen (Notiz reinziehen). */}
      <aside className="hidden md:block border-r border-border bg-surface/40 overflow-y-auto p-2 space-y-3">
        <SidebarRow
          icon={<Inbox className="w-4 h-4" />}
          label={t.notes.alleNotizen}
          active={filter.kind === "all"}
          onClick={() => setFilter({ kind: "all" })}
          count={notes.length}
          dropId={DROP_ALL}
        />
        <div>
          <div className="px-2 text-xs uppercase tracking-wider text-text-3 mb-1">
            {t.notes.kategorien}
          </div>
          {cats.map((c) => (
            <SidebarRow
              key={c.id}
              icon={<Folder className="w-4 h-4 text-warning" />}
              label={c.name}
              active={filter.kind === "cat" && filter.id === c.id}
              onClick={() => setFilter({ kind: "cat", id: c.id })}
              dropId={c.id}
            />
          ))}
          {cats.length === 0 && (
            <div className="text-[11px] text-text-3 px-2 py-1">
              {t.notes.keineKategorienHinweis}
            </div>
          )}
        </div>
        {allTags.length > 0 && (
          <div>
            <div className="px-2 text-xs uppercase tracking-wider text-text-3 mb-1">
              {t.notes.tags}
            </div>
            {allTags.map((t) => (
              <SidebarRow
                key={t}
                icon={<Hash className="w-4 h-4 text-brand-500" />}
                label={t}
                active={filter.kind === "tag" && filter.t === t}
                onClick={() => setFilter({ kind: "tag", t })}
              />
            ))}
          </div>
        )}
      </aside>

      {/* Spalte 2: Notes-Liste - mobile-toggle via noteId, desktop immer sichtbar */}
      <section
        className={clsx(
          "border-r border-border flex-col overflow-hidden bg-surface/20 md:flex",
          noteId ? "hidden md:flex" : "flex"
        )}
      >
        {/* Mobile-Filter-Row: aktiver Filter als Chip + Filter-Icon (Modal) */}
        <div className="md:hidden flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
          <div className="flex-1 min-w-0 flex items-center gap-1.5">
            <span className="px-2 py-1 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-sm font-medium truncate">
              {filterLabel}
            </span>
            <span className="text-xs text-text-3 tabular-nums">{notes.length}</span>
          </div>
          <button
            onClick={() => setFilterModalOpen(true)}
            className="flex items-center justify-center min-h-11 min-w-11 rounded-md hover:bg-surface-3 text-text"
            aria-label={t.notes.filterOeffnen}
            title={t.notes.filter}
          >
            <FilterIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 p-2 border-b border-border">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-text-3 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.notes.suchePlatzhalter}
              aria-label={t.notes.sucheAria}
              className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md bg-surface-3 focus:outline-none"
            />
          </div>
          <button
            onClick={newNote}
            title={t.notes.neueNotiz}
            aria-label={t.notes.neueNotiz}
            className="flex items-center justify-center min-h-11 min-w-11 md:min-h-0 md:min-w-0 md:p-1.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {notes.length === 0 && (
            <EmptyState
              className="py-8 px-4"
              icon={<Sparkles className="w-6 h-6" />}
              title={t.notes.keineNotizen}
              hint={t.notes.ersteNotizAnlegen}
              action={
                <Button variant="secondary" onClick={newNote}>
                  {t.notes.neueNotiz}
                </Button>
              }
            />
          )}
          {notes.map((n) => (
            <NoteListItem
              key={n.id}
              note={n}
              active={n.id === noteId}
              draggable={isDesktop}
              onOpen={() => navigate(`/n/${n.id}`)}
            />
          ))}
        </div>
      </section>

      {/* Mobile-Filter-Modal: zentriert, schliesst nach Auswahl */}
      {filterModalOpen && (
        <FilterModal
          cats={cats}
          tags={allTags}
          notesCount={notes.length}
          filter={filter}
          onSelect={(f) => {
            setFilter(f);
            setFilterModalOpen(false);
          }}
          onClose={() => setFilterModalOpen(false)}
        />
      )}

      {/* Spalte 3: Editor - mobile-toggle via noteId, desktop immer sichtbar */}
      <section
        className={clsx(
          "overflow-hidden bg-surface",
          noteId ? "block" : "hidden md:block"
        )}
      >
        {selected ? (
          <NoteEditor
            note={selected}
            cats={cats}
            onBack={() => navigate("/n")}
            onSaved={(updated) =>
              setNotes((s) => s.map((n) => (n.id === updated.id ? updated : n)))
            }
            onDelete={() => delNote(selected)}
            onPromoted={(taskId) =>
              setNotes((s) =>
                s.map((n) =>
                  n.id === selected.id ? { ...n, promoted_task_id: taskId } : n
                )
              )
            }
          />
        ) : (
          <div className="h-full grid place-items-center">
            <EmptyState
              icon={<Sparkles className="w-6 h-6" />}
              title={t.notes.keineNotizAusgewaehlt}
              hint={t.notes.keineNotizAusgewaehltHinweis}
              action={
                <Button variant="secondary" onClick={newNote}>
                  {t.notes.neueNotiz}
                </Button>
              }
            />
          </div>
        )}
      </section>
    </div>
    {/* Kompakte Titel-Karte, die am Cursor haengt, waehrend gezogen wird. */}
    <DragOverlay dropAnimation={null}>
      {dragNote ? (
        <div className="px-3 py-2 rounded-md bg-surface border border-border-strong shadow-lg text-sm font-medium max-w-[260px] truncate cursor-grabbing">
          {dragNote.title ||
            dragNote.body_md.split("\n")[0].slice(0, 60) ||
            t.notes.leer}
        </div>
      ) : null}
    </DragOverlay>
   </DndContext>
  );
}

function NoteListItem({
  note,
  active,
  draggable,
  onOpen,
}: {
  note: Note;
  active: boolean;
  draggable: boolean;
  onOpen: () => void;
}) {
  const t = useT();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: note.id,
    disabled: !draggable,
  });
  const title = note.title || note.body_md.split("\n")[0].slice(0, 80) || t.notes.leer;
  const preview = note.body_md.split("\n").slice(1).join(" ").slice(0, 80);
  return (
    <button
      ref={setNodeRef}
      onClick={onOpen}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      className={clsx(
        "w-full px-3 py-2 text-left border-b border-border",
        draggable && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-40",
        active ? "bg-brand-50 dark:bg-brand-700/15" : "hover:bg-surface-2"
      )}
    >
      <div className="text-sm font-medium truncate">{title}</div>
      <div className="text-xs text-text-2 truncate mt-0.5">
        <span>{new Date(note.updated_at).toLocaleDateString("de-DE")}</span>
        {preview && (
          <>
            {" · "}
            {preview}
          </>
        )}
      </div>
      {note.tags.length > 0 && (
        <div className="text-[11px] text-brand-600 dark:text-brand-400 mt-1 truncate">
          {note.tags.map((t) => `#${t}`).join(" ")}
        </div>
      )}
    </button>
  );
}

function FilterModal({
  cats, tags, notesCount, filter, onSelect, onClose,
}: {
  cats: Category[];
  tags: string[];
  notesCount: number;
  filter: Filter;
  onSelect: (f: Filter) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Modal onClose={onClose} title={t.notes.notizenFiltern} size="sm" bodyClassName="flex-1 overflow-y-auto p-2">
        <div>
          <FilterRow
            icon={<Inbox className="w-4 h-4" />}
            label={t.notes.alleNotizen}
            badge={notesCount}
            active={filter.kind === "all"}
            onClick={() => onSelect({ kind: "all" })}
          />
          {cats.length > 0 && (
            <div className="mt-3">
              <div className="px-3 text-xs uppercase tracking-wider text-text-3 mb-1">{t.notes.kategorien}</div>
              {cats.map((c) => (
                <FilterRow
                  key={c.id}
                  icon={<Folder className="w-4 h-4 text-warning" />}
                  label={c.name}
                  active={filter.kind === "cat" && filter.id === c.id}
                  onClick={() => onSelect({ kind: "cat", id: c.id })}
                />
              ))}
            </div>
          )}
          {tags.length > 0 && (
            <div className="mt-3">
              <div className="px-3 text-xs uppercase tracking-wider text-text-3 mb-1">{t.notes.tags}</div>
              {tags.map((tag) => (
                <FilterRow
                  key={tag}
                  icon={<Hash className="w-4 h-4 text-brand-500" />}
                  label={tag}
                  active={filter.kind === "tag" && filter.t === tag}
                  onClick={() => onSelect({ kind: "tag", t: tag })}
                />
              ))}
            </div>
          )}
          {cats.length === 0 && tags.length === 0 && (
            <div className="text-[11px] text-text-3 px-3 py-3 text-center">
              {t.notes.keineKategorienOderTags}
            </div>
          )}
        </div>
    </Modal>
  );
}

function FilterRow({
  icon, label, badge, active, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "w-full flex items-center gap-3 px-3 min-h-11 rounded-md text-sm text-left",
        active
          ? "bg-brand-100 dark:bg-brand-900/30 text-brand-800 dark:text-brand-200 font-medium"
          : "hover:bg-surface-3 text-text"
      )}
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {badge !== undefined && (
        <span className="text-xs text-text-3 tabular-nums">{badge}</span>
      )}
    </button>
  );
}

function SidebarRow({
  icon,
  label,
  active,
  onClick,
  count,
  dropId,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  count?: number;
  /** Wenn gesetzt, ist die Zeile Drop-Zone fuer Notiz→Kategorie. */
  dropId?: string;
}) {
  // Hook immer aufrufen (Regeln der Hooks); nur nutzen, wenn dropId gesetzt ist.
  const { setNodeRef, isOver } = useDroppable({ id: dropId ?? "__noop__", disabled: !dropId });
  return (
    <button
      ref={dropId ? setNodeRef : undefined}
      onClick={onClick}
      className={clsx(
        "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left",
        dropId && isOver && "ring-2 ring-accent-300 bg-accent-50 dark:bg-accent-500/15",
        active
          ? "bg-surface-3 text-text dark:text-white font-medium"
          : "hover:bg-surface-3 text-text-2"
      )}
    >
      {icon}
      <span className="truncate flex-1">{label}</span>
      {count !== undefined && (
        <span className="text-xs text-text-3">{count}</span>
      )}
    </button>
  );
}

function NoteEditor({
  note,
  cats,
  onBack,
  onSaved,
  onDelete,
  onPromoted,
}: {
  note: Note;
  cats: Category[];
  onBack: () => void;
  onSaved: (n: Note) => void;
  onDelete: () => void;
  onPromoted: (taskId: string) => void;
}) {
  const [title, setTitle] = useState(note.title ?? "");
  const [body, setBody] = useState(note.body_md);
  const [tagInput, setTagInput] = useState("");
  const [tagOpen, setTagOpen] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const t = useT();
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTitle(note.title ?? "");
    setBody(note.body_md);
    setSavedAt(null);
    setTagOpen(false);
    setTagInput("");
  }, [note.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (title === (note.title ?? "") && body === note.body_md) return;
    const h = setTimeout(async () => {
      setSaving(true);
      try {
        const updated = await api.patch<Note>(`/notes/${note.id}`, {
          title: title || null,
          body_md: body,
        });
        onSaved(updated);
        setSavedAt(new Date());
      } catch (e) {
        toast.err(t.notes.konnteNichtSpeichern, (e as Error).message);
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => clearTimeout(h);
  }, [title, body, note.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cat = cats.find((c) => c.id === note.category_id);

  const promote = async () => {
    try {
      const task = await api.post<{ id: string }>(`/notes/${note.id}/promote`);
      onPromoted(task.id);
      toast.ok(t.notes.notizInAufgabeUmgewandelt);
    } catch (e) {
      toast.err(t.notes.promoteFehlgeschlagen, (e as Error).message);
    }
  };

  const addTag = async (raw: string) => {
    const t = raw.replace(/^#/, "").trim().toLowerCase();
    if (!t || note.tags.includes(t)) {
      setTagInput("");
      return;
    }
    const updated = await api.patch<Note>(`/notes/${note.id}`, {
      tags: [...note.tags, t],
    });
    onSaved(updated);
    setTagInput("");
  };

  const removeTag = async (t: string) => {
    const updated = await api.patch<Note>(`/notes/${note.id}`, {
      tags: note.tags.filter((x) => x !== t),
    });
    onSaved(updated);
  };

  return (
    <div className="h-full flex flex-col">
      <header className="px-3 md:px-6 py-2 md:py-3 border-b border-border flex items-center gap-2 md:gap-3 flex-wrap">
        <button
          onClick={onBack}
          className="md:hidden flex items-center justify-center min-h-11 min-w-11 rounded-md hover:bg-surface-3 text-text"
          aria-label={t.notes.zurueckZurListe}
          title={t.notes.zurueck}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="flex-1 text-xs text-text-2 truncate">
          {saving ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> {t.notes.wirdGespeichert}
            </span>
          ) : savedAt ? (
            t.notes.gespeichertUm(savedAt.toLocaleTimeString("de-DE"))
          ) : (
            t.notes.bearbeitetAm(new Date(note.updated_at).toLocaleString("de-DE"))
          )}
        </span>
        {note.promoted_task_id ? (
          <span className="text-xs text-success inline-flex items-center gap-1">
            <CheckSquare className="w-3 h-3" /> {t.notes.aufgabeVerlinkt}
          </span>
        ) : (
          <button
            onClick={promote}
            className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
            title={t.notes.inAufgabeUmwandeln}
          >
            <CheckSquare className="w-3 h-3" /> {t.notes.zuAufgabeMachen}
          </button>
        )}
        <button
          onClick={onDelete}
          className="text-xs text-danger hover:underline inline-flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" /> {t.common.loeschen}
        </button>
      </header>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {/* Kategorie read-only als dezenter Chip — Aenderung via Drag in der Liste. */}
        {cat && (
          <div className="mb-2 inline-flex items-center gap-1 text-xs text-text-2">
            <Folder className="w-3.5 h-3.5 text-text-3" />
            {cat.name}
          </div>
        )}
        <input
          ref={titleRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t.notes.titelPlatzhalter}
          aria-label={t.notes.notizTitelAria}
          className="w-full text-2xl font-semibold bg-transparent focus:outline-none placeholder:text-text-3"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t.notes.inhaltPlatzhalter}
          aria-label={t.notes.notizInhaltAria}
          rows={1}
          onInput={(e) => {
            const el = e.currentTarget;
            el.style.height = "auto";
            el.style.height = el.scrollHeight + "px";
          }}
          className="w-full mt-4 bg-transparent focus:outline-none resize-none leading-relaxed text-[15px]"
        />
        {/* Tag-Diaet: dezente Chips (text-2) unter dem Inhalt + kleines Ghost-„+".
            Kein schwerer Block/Platzhalter — ohne Tags nur das Ghost-Icon. */}
        <div className="mt-8 flex items-center gap-x-3 gap-y-1 flex-wrap">
          {note.tags.map((tag) => (
            <span key={tag} className="group inline-flex items-center gap-1 text-xs text-text-2">
              <span className="text-text-3">#</span>
              {tag}
              <button
                onClick={() => removeTag(tag)}
                aria-label={t.notes.tagEntfernen(tag)}
                className="text-text-3 hover:text-danger opacity-0 group-hover:opacity-100 focus:opacity-100"
              >
                ×
              </button>
            </span>
          ))}
          {tagOpen ? (
            <input
              autoFocus
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onBlur={() => {
                if (!tagInput.trim()) setTagOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " " || e.key === ",") {
                  e.preventDefault();
                  addTag(tagInput);
                } else if (e.key === "Escape") {
                  setTagInput("");
                  setTagOpen(false);
                } else if (e.key === "Backspace" && !tagInput && note.tags.length) {
                  removeTag(note.tags[note.tags.length - 1]);
                }
              }}
              placeholder={t.notes.tagPlatzhalter}
              aria-label={t.notes.tagHinzufuegenAria}
              className="text-xs bg-transparent focus:outline-none w-24 border-b border-border"
            />
          ) : (
            <button
              onClick={() => setTagOpen(true)}
              aria-label={t.notes.tagHinzufuegenAria}
              title={t.notes.tagHinzufuegenTitel}
              className="inline-flex items-center text-text-3 hover:text-text-2"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
